import type { TreeOption } from 'naive-ui'
import type {
  AssemblyComponentFixationState,
  GlbHierarchyNode,
  EntityHighlightTarget,
  GlbDatumSlotKind,
  GlbNodeRole,
} from '@puppycad/cad-core'
import {
  AssemblyComponentFixationService,
  resolveGlbHierarchyNodeIconType,
  resolveGlbHierarchyNodeSemanticKind,
} from '@puppycad/cad-core'
import {
  createFeatureManagerAnnotationsNode,
  createFeatureManagerBodyFolderNode,
  createFeatureManagerHistoryNode,
  createFeatureManagerMaterialNode,
} from './feature-manager-system-nodes'

export type { GlbNodeRole }

export interface GlbFeatureTreeNodeBinding {
  key: string
  objectId: string
  /**
   * 装配实例节点 ID / 项目模型 nodeKey。
   *
   * 设计树与视口右键都依赖该稳定 ID，把命中的拓扑代理重新映射回组件节点；
   * 项目装配容器节点也使用它生成稳定树 key，避免 Undo 后丢失展开状态。
   */
  assemblyNodeId?: string
  selectionObjectId: string | null
  objectType: string
  /** 节点语义角色，简单明确地表明该节点是装配/零件/实体/实体文件夹等 */
  glbNodeRole: GlbNodeRole
  glbPath: string
  selectable: boolean
  displayName?: string
  /** 不含固定状态、配置与显示状态的装配节点权威名称。 */
  assemblyNodeDisplayName?: string
  /** imported-model 层级解析出的 SOLIDWORKS 组件实例序号。 */
  assemblyNodeInstanceIndex?: number
  externalFileUuid?: string
  externalAssemblyName?: string
  /** 装配体零部件固定/浮动状态。 */
  fixationState?: AssemblyComponentFixationState
  /** 是否允许在设计树中执行固定/浮动。 */
  canFixOrFloat?: boolean
  /** 该实体对应的 glTF primitives 下标集合，用于设计树 hover/select 高亮精确几何 */
  highlightTargets?: EntityHighlightTarget[]
  /**
   * 导入 body 的运行时绑定 ID。
   *
   * Body 节点是设计树虚拟节点，视口中的 Object3D 可能由多个 Body 共用。该字段用于
   * 视口点/线/面选择回流、baseline 消耗隐藏和实体节点去重。
   */
  bodyBindingId?: string
  /**
   * Worker GeometryState 中该导入基线体的权威对象 ID。
   *
   * 该字段直接来自 cad-core 的 geometryObjectId，设计树不得根据 bodyBindingId 自行派生。
   */
  geometryObjectId?: string
}

export interface GlbFeatureTreeObjectKeyBinding {
  objectId: string
  key: string
}

export interface GlbFeatureTreeDatumSlotBinding {
  key: string
  tabId: string
  hostObjectId: string
  slotKind: GlbDatumSlotKind
}

/**
 * 注入到 GLB 设计树根节点的场景草图条目。
 * 该结构刻意保持精简，避免与 hooks 层的 ManagedSketchSummary 类型直接耦合，
 * 使 src/core 不依赖 src/hooks 中的特征树类型。
 */
export interface GlbFeatureManagerSceneSketchEntry {
  /** 草图对象 ID（viewer.listSketchGroupSummaries 返回的 id）。 */
  id: string
  /** 草图显示名称。 */
  name: string
  /** 是否隐藏。 */
  hidden?: boolean
  /** 是否压制。 */
  suppressed?: boolean
}

/**
 * 注入的草图节点绑定。
 * 上层（useImportedModelFeatureTree）据此把节点写入 featureTreeNodeMap，
 * 使草图可被选中、右键编辑、与原生投影路径行为一致。
 */
export interface GlbFeatureTreeSketchNodeBinding {
  key: string
  objectId: string
}

export interface GlbFeatureManagerTreeBuildResult {
  tree: TreeOption[]
  glbNodeBindings: GlbFeatureTreeNodeBinding[]
  // 实体(Body)子节点的 glb-node binding，用于设计树 hover/select 高亮
  bodyNodeBindings: GlbFeatureTreeNodeBinding[]
  datumSlotBindings: GlbFeatureTreeDatumSlotBinding[]
  objectKeyBindings: GlbFeatureTreeObjectKeyBinding[]
  groupKeys: string[]
  /** 注入到根节点下的草图节点绑定列表。 */
  sketchNodeBindings: GlbFeatureTreeSketchNodeBinding[]
}

interface GlbFeatureManagerTreeBuildOptions {
  tabId: string
  hierarchy: GlbHierarchyNode
  rootLabel?: string
  rootIconType?: 'assembly' | 'part' | 'group'
  /**
   * 注入到树根的场景草图列表。
   * 仅追加到 effectiveHierarchy 根节点下的子节点末尾，使 GLB 静态结构
   * 与 viewer 的动态草图共存；子树构建（insertImportedModel 路径）通常不传入。
   */
  sceneSketches?: GlbFeatureManagerSceneSketchEntry[]
}

interface GlbTreeBuildContext {
  glbNodeBindings: GlbFeatureTreeNodeBinding[]
  // 实体(Body)子节点的 binding，独立于 glbNodeBindings 以便 Editor.vue 区分处理
  bodyNodeBindings: GlbFeatureTreeNodeBinding[]
  datumSlotBindings: GlbFeatureTreeDatumSlotBinding[]
  objectKeyBindings: GlbFeatureTreeObjectKeyBinding[]
  groupKeys: string[]
  /** Part 实体节点是否必须使用 Worker 几何修订 ID 作为唯一选择身份。 */
  useAuthoritativeBodySelection: boolean
}

const DEFAULT_ROOT_LABEL = '装配/文件'

/**
 * 线性零部件阵列运行时节点类型。
 */
enum LinearComponentPatternRuntimeNodeType {
  /** 阵列组节点。 */
  Group = 'LinearPatternRuntimeGroup',
  /** 阵列零件节点。 */
  Part = 'LinearPatternRuntimePart',
}

/**
 * 解析项目模型容器节点的稳定设计树身份。
 *
 * @param node GLB hierarchy 节点。
 * @returns 可跨 Undo/Redo 复用的结构身份；非项目模型容器节点返回 null。
 */
function resolveStableProjectModelNodeIdentity(node: GlbHierarchyNode): string | null {
  const assemblyNodeId = node.meta?.assemblyNodeId?.trim() ?? ''
  if (assemblyNodeId.length === 0) {
    return null
  }

  const semanticKind = resolveGlbHierarchyNodeSemanticKind(node)
  if (semanticKind !== 'assembly' && semanticKind !== 'part') {
    return null
  }

  return `assembly-node::${assemblyNodeId}`
}

/**
 * 创建 GLB FeatureManager 节点 key。
 *
 * @param tabId 当前树归属标签。
 * @param node GLB hierarchy 节点。
 * @returns Naive Tree 使用的稳定 key。
 */
function buildGlbTreeNodeKey(tabId: string, node: GlbHierarchyNode): string {
  const stableIdentity = resolveStableProjectModelNodeIdentity(node)
  const identity = stableIdentity ?? node.objectId

  return `glb-node::${tabId}::${identity}`
}

function registerGroupKey(context: GlbTreeBuildContext, key: string): void {
  if (!context.groupKeys.includes(key)) {
    context.groupKeys.push(key)
  }
}

/**
 * 判断当前节点是否为线性零部件阵列运行时节点。
 *
 * 设计说明：
 * 阵列运行时节点应只保留业务子节点（阵列零部件），
 * 不注入历史记录/基准面/原点等系统子节点，保持与 SolidWorks 阵列树结构一致。
 */
function isLinearComponentPatternRuntimeNode(node: GlbHierarchyNode): boolean {
  return node.nodeType === LinearComponentPatternRuntimeNodeType.Group
    || node.nodeType === LinearComponentPatternRuntimeNodeType.Part
}

//------ 线性阵列来源对象粉色高亮 开始------
/**
 * 读取运行时阵列复制体节点的源对象 ID。
 *
 * @param node GLB hierarchy 节点。
 * @returns 源对象 ID；节点不携带来源时返回空字符串。
 */
function resolveLinearComponentPatternSourceObjectId(node: GlbHierarchyNode): string {
  const sourceObjectId = node.meta?.linearComponentPatternSourceObjectId?.trim() ?? ''
  return sourceObjectId
}

/**
 * 解析线性阵列分组节点关联的源对象 ID 列表。
 *
 * 设计说明：
 * 1. 首选同步入口写入的分组级来源列表，避免 UI 层遍历业务记录；
 * 2. 兼容旧运行时数据，分组级字段缺失时从直接子复制体节点兜底汇总；
 * 3. 只返回去重后的非空 ID，保证后续交互元信息稳定且不含脏值。
 *
 * @param node GLB hierarchy 节点。
 * @returns 源对象 ID 列表；非阵列分组或无来源时返回 undefined。
 */
function resolveLinearComponentPatternSourceObjectIds(
  node: GlbHierarchyNode,
): string[] | undefined {
  if (node.nodeType !== LinearComponentPatternRuntimeNodeType.Group) {
    return undefined
  }

  const sourceObjectIdSet = new Set<string>()
  const metaSourceObjectIds = Array.isArray(node.meta?.linearComponentPatternSourceObjectIds)
    ? node.meta.linearComponentPatternSourceObjectIds
    : []

  metaSourceObjectIds.forEach((sourceObjectId) => {
    const normalizedSourceObjectId = sourceObjectId.trim()
    if (normalizedSourceObjectId.length > 0) {
      sourceObjectIdSet.add(normalizedSourceObjectId)
    }
  })

  if (sourceObjectIdSet.size === 0) {
    node.children.forEach((child) => {
      const sourceObjectId = resolveLinearComponentPatternSourceObjectId(child)
      if (sourceObjectId.length > 0) {
        sourceObjectIdSet.add(sourceObjectId)
      }
    })
  }

  return sourceObjectIdSet.size > 0 ? [...sourceObjectIdSet] : undefined
}
//------ 线性阵列来源对象粉色高亮 结束------

//------ 线性零部件阵列设计树禁用态修复 开始------
/**
 * 判断 GLB 设计树节点是否需要禁用。
 *
 * 设计说明：
 * 1. `selectable` 表示节点是否能绑定到场景几何选择，不等同于树节点是否可展开或可交互；
 * 2. 线性零部件阵列运行时分组本身没有几何选择锚点，但在设计树中应保持正常显示和展开；
 * 3. 普通无几何 GLB 节点仍保持禁用，避免无意义节点进入选择链路。
 *
 * @param node GLB hierarchy 节点。
 * @param selectable 节点是否可绑定场景选择。
 * @returns 若树节点应禁用，则返回 `true`。
 */
function shouldDisableGlbTreeNode(node: GlbHierarchyNode, selectable: boolean): boolean {
  if (isLinearComponentPatternRuntimeNode(node)) {
    return false
  }
  return !selectable
}

/**
 * 解析设计树节点的 hover 锚点对象 ID。
 *
 * 设计说明：
 * 1. 普通 imported-model 节点继续使用 `selectionObjectId`，保持既有 hover/选中语义不变；
 * 2. 线性零部件阵列运行时分组需要高亮整组阵列实例，不能退化为首个子零件的选择锚点；
 * 3. 因此阵列分组节点改为直接指向运行时 Group 自身，使 bbox 基于整组对象计算。
 *
 * @param node GLB hierarchy 节点。
 * @param selectionObjectId 常规选择锚点。
 * @returns 设计树 hover 应使用的对象 ID。
 */
function resolveTreeNodeRefObjectId(
  node: GlbHierarchyNode,
  selectionObjectId: string | null,
): string | null {
  if (node.nodeType === LinearComponentPatternRuntimeNodeType.Group) {
    return node.objectId
  }
  return selectionObjectId
}
//------ 线性零部件阵列设计树禁用态修复 结束------

function normalizeNodeName(node: GlbHierarchyNode): string {
  const name = node.name?.trim()
  if (name && name.length > 0) {
    return name
  }
  const pathSegments = node.path.split('/')
  const leafPath = pathSegments[pathSegments.length - 1] ?? ''
  if (leafPath.length > 0) {
    return leafPath
  }
  return node.nodeType || '未命名组件'
}

/**
 * 解析装配节点用于其它 cad-core 投影的权威基础名称。
 *
 * 设计说明：
 * 设计树标题还会追加固定状态、配置和显示状态，不能反向解析成 Mate 参与者名称；这里在追加
 * 这些 UI 信息之前保留 imported-model 的语义名称，供 Mate 等跨模块投影复用。
 *
 * @param node GLB hierarchy 节点。
 * @returns 不含 UI 状态修饰的装配节点名称。
 */
function resolveAssemblyNodeDisplayName(node: GlbHierarchyNode): string {
  const externalAssemblyName = node.meta?.externalAssemblyName?.trim()
  if (externalAssemblyName && node.meta?.externalFileUuid) {
    return externalAssemblyName
  }
  return normalizeNodeName(node)
}


/**
 * 判断设计树节点是否需要显示固定/浮动状态。
 *
 * @param node GLB hierarchy 节点。
 * @returns 节点是否代表装配体中的零部件实例。
 */
function shouldShowComponentFixationState(node: GlbHierarchyNode): boolean {
  return AssemblyComponentFixationService.isFixableComponentNode(node)
    && (node.parentObjectId !== null || Boolean(node.meta?.externalFileUuid))
}

function formatComponentTitle(node: GlbHierarchyNode): string {
  const titleParts: string[] = []

  let mainTitle = resolveAssemblyNodeDisplayName(node)
  if (shouldShowComponentFixationState(node)) {
    mainTitle = AssemblyComponentFixationService.formatComponentTreeLabel({
      label: mainTitle,
      fixationState: AssemblyComponentFixationService.resolveHierarchyNodeFixationState(node),
    })
  }
  titleParts.push(mainTitle)

  const configurationName = node.meta?.configurationName?.trim()
  if (configurationName) {
    titleParts.push(`<${configurationName}>`)
  }

  const displayStateName = node.meta?.displayStateName?.trim()
  if (displayStateName) {
    titleParts.push(`<${displayStateName}>`)
  }

  return titleParts.join(' ')
}

/**
 * 创建与完整构树相同的单节点展示字段，供固定状态增量更新复用。
 * @param tabId 当前树归属标签。
 * @param node SDK 返回的最新层级节点。
 * @returns 只含稳定 key、标题和固定操作状态的 UI 数据。
 */
export function createGlbNodePresentationUpdate(
  tabId: string,
  node: GlbHierarchyNode,
): GlbTreeNodePresentationUpdate {
  const canFixOrFloat = shouldShowComponentFixationState(node)
  return {
    key: buildGlbTreeNodeKey(tabId, node),
    displayName: formatComponentTitle(node),
    canFixOrFloat,
    fixationState: canFixOrFloat
      ? AssemblyComponentFixationService.resolveHierarchyNodeFixationState(node)
      : undefined,
  }
}

function resolveNodeObjectType(node: GlbHierarchyNode): string {
  if (node.nodeType === 'Body') {
    return 'body'
  }
  const semanticKind = resolveGlbHierarchyNodeSemanticKind(node)
  return semanticKind === 'unknown' ? node.nodeType : semanticKind
}

/**
 * 根据节点语义判断设计树节点角色。
 * - assembly: 装配体节点
 * - part: 零件节点
 * - body: 实体节点（虚拟 Body，对应具体几何）
 * - component: 组件节点（有几何但 semanticKind 不明确）
 * - group: 纯组织节点（无几何）
 */
export function resolveNodeRole(node: GlbHierarchyNode): GlbNodeRole {
  if (node.nodeType === 'Body') return 'body'
  const semanticKind = resolveGlbHierarchyNodeSemanticKind(node)
  if (semanticKind === 'assembly') return 'assembly'
  if (semanticKind === 'part') return 'part'
  if (node.meta?.hasGeometry) return 'component'
  return 'group'
}

/**
 * 聚合 GLB 层级节点的高亮目标。
 *
 * 设计说明：
 * 1. 零件/零部件节点本身通常不直接挂 highlightTargets；
 * 2. 测量与设计树选中需要从 bodyHighlightTargetsById 汇总全部实体 mesh 目标。
 *
 * @param node GLB 层级节点。
 * @returns 高亮目标列表；无可用目标时返回 undefined。
 */
export function resolveGlbHierarchyHighlightTargets(
  node: GlbHierarchyNode,
): EntityHighlightTarget[] | undefined {
  const directTargets = node.meta?.highlightTargets
  if (Array.isArray(directTargets) && directTargets.length > 0) {
    return directTargets.map((target) => ({
      objectId: target.objectId,
      primitiveIndices: Array.isArray(target.primitiveIndices)
        ? [...target.primitiveIndices]
        : undefined,
    }))
  }

  const targetsByBodyId = node.meta?.bodyHighlightTargetsById
  if (!targetsByBodyId || typeof targetsByBodyId !== 'object') {
    return undefined
  }

  const aggregatedTargets = Object.values(targetsByBodyId).flatMap((targets) => (
    Array.isArray(targets)
      ? targets.map((target) => ({
          objectId: target.objectId,
          primitiveIndices: Array.isArray(target.primitiveIndices)
            ? [...target.primitiveIndices]
            : undefined,
        }))
      : []
  ))

  return aggregatedTargets.length > 0 ? aggregatedTargets : undefined
}

function resolveMaterialLabel(node: GlbHierarchyNode): string | null {
  const explicitMaterialName = node.meta?.materialName?.trim()
  if (explicitMaterialName) {
    return explicitMaterialName
  }

  const materialNames = Array.isArray(node.meta?.materialNames)
    ? node.meta!.materialNames.map((name) => String(name).trim()).filter((name) => name.length > 0)
    : []
  if (materialNames.length === 1) {
    return materialNames[0]
  }
  if (materialNames.length > 1) {
    return materialNames.join(' / ')
  }
  return null
}

function isSyntheticPrimitiveNode(node: GlbHierarchyNode): boolean {
  return node.nodeType === 'Mesh' && /^node:\d+#primitive:\d+$/.test(node.name?.trim() ?? '')
}

/**
 * 构建 FeatureManager 顶部系统节点。
 *
 * @param parentKey 父级树节点 key。
 * @param context GLB 树构建上下文。
 * @returns 历史记录与注解节点。
 */
function buildDefaultSystemHeaderChildren(
  parentKey: string,
  context: GlbTreeBuildContext,
): TreeOption[] {
  const historyKey = `${parentKey}::history`
  const annotationsKey = `${parentKey}::annotations`

  ;[
    historyKey,
    annotationsKey,
  ].forEach((key) => registerGroupKey(context, key))

  return [
    createFeatureManagerHistoryNode(historyKey),
    createFeatureManagerAnnotationsNode(annotationsKey),
  ]
}

/**
 * 构建默认三基准面与原点槽位节点。
 *
 * @param tabId 当前标签 ID。
 * @param parentKey 父级树节点 key。
 * @param hostObjectId 槽位绑定的宿主对象 ID。
 * @param context GLB 树构建上下文。
 * @returns 基准槽位节点列表。
 */
function buildDefaultDatumSlotChildren(
  tabId: string,
  parentKey: string,
  hostObjectId: string,
  context: GlbTreeBuildContext,
): TreeOption[] {
  const datumSlotEntries = [
    { suffix: 'front-plane', label: '前视基准面', iconType: 'datum-plane', slotKind: 'front-plane' as const },
    { suffix: 'top-plane', label: '上视基准面', iconType: 'datum-plane', slotKind: 'top-plane' as const },
    { suffix: 'right-plane', label: '右视基准面', iconType: 'datum-plane', slotKind: 'right-plane' as const },
    { suffix: 'origin', label: '原点', iconType: 'origin', slotKind: 'origin' as const },
  ]

  return datumSlotEntries.map((entry) => {
    const key = `${parentKey}::${entry.suffix}`
    context.datumSlotBindings.push({
      key,
      tabId,
      hostObjectId,
      slotKind: entry.slotKind,
    })
    return {
      key,
      label: entry.label,
      type: 'glb-datum-slot',
      iconType: entry.iconType,
      isLeaf: true,
      disabled: false,
      refObjectId: hostObjectId,
    } as TreeOption
  })
}

function buildPartSpecificChildren(
  parentKey: string,
  node: GlbHierarchyNode,
  context: GlbTreeBuildContext,
  keyBase = parentKey,
  // GLB 层级提供的显示对象 UUID；只负责装配树选择或纯导入体的显示分区绑定。
  selectionObjectId: string | null = null,
): TreeOption[] {
  const children: TreeOption[] = []
  const bodyEntries = Array.isArray(node.meta?.bodyEntries) ? node.meta.bodyEntries : []

  if (bodyEntries.length > 0) {
    const bodyFolderKey = `${keyBase}::bodies`
    registerGroupKey(context, bodyFolderKey)
    const bodyChildren = bodyEntries.map((bodyEntry, index) => {
      const bodyKey = `${bodyFolderKey}::${index + 1}`
      // 从当前零件子树中递归查找对应的 Body 虚拟节点；
      // 装配场景下 Body 节点可能位于引用零件 hierarchy 的更深层，而不是当前 node 的直接 children。
      const bodyNode = findBodyNodeByBodyId(node, bodyEntry.bodyId)
      const highlightTargets = bodyNode?.meta?.highlightTargets
        ?? node.meta?.bodyHighlightTargetsById?.[bodyEntry.bodyId]
      const displaySelectionObjectId = resolveNodeSelectionObjectId(bodyNode ?? null) ?? selectionObjectId
      const geometryObjectId = bodyEntry.geometryObjectId?.trim() || null
      /**
       * 零件标签下的实体文件夹属于 CAD 主状态投影，必须直接绑定 Worker 权威修订 ID。
       * GLB UUID 只保留为显示来源，不能在特征重放交接显示所有权后继续充当逻辑实体身份。
       * 装配体与分组仍沿用 GLB 实例 UUID，保持 replaceTree 的实例选择语义不变。
       */
      const bodySelectionObjectId = context.useAuthoritativeBodySelection
        ? geometryObjectId
        : displaySelectionObjectId
      const bodyDisplayObjectId = displaySelectionObjectId ?? geometryObjectId
      // 不再用 registerGroupKey，而是注册为 bodyNodeBinding，以便 Editor.vue 写入 featureTreeNodeMap
      if (bodySelectionObjectId && bodyDisplayObjectId) {
        context.bodyNodeBindings.push({
          key: bodyKey,
          objectId: bodyDisplayObjectId,
          assemblyNodeId: node.meta?.assemblyNodeId?.trim() || undefined,
          selectionObjectId: bodySelectionObjectId,
          objectType: 'body',
          glbNodeRole: 'body',
          bodyBindingId: bodyEntry.bodyBindingId ?? bodyEntry.bodyId,
          geometryObjectId: geometryObjectId ?? undefined,
          glbPath: node.path,
          selectable: true,
          displayName: bodyEntry.label,
          highlightTargets,
        })
      } else {
        // 无 selectionObjectId 时降级为 group，保持原有行为
        registerGroupKey(context, bodyKey)
      }
      return {
        key: bodyKey,
        label: bodyEntry.label,
        type: 'glb-node',
        iconType: 'body',
        isLeaf: true,
        // 与点击链使用同一权威身份，避免 hover 再回退到已释放的 GLB UUID。
        refObjectId: bodySelectionObjectId ?? undefined,
      } as TreeOption
    })
    children.push(createFeatureManagerBodyFolderNode({
      key: bodyFolderKey,
      bodyNodes: bodyChildren,
    }))
  }

  const nodeRole = resolveNodeRole(node)
  if (nodeRole === 'part' || nodeRole === 'component') {
    const materialLabel = resolveMaterialLabel(node)
    if (materialLabel || bodyEntries.length > 0) {
      const materialKey = `${keyBase}::material`
      registerGroupKey(context, materialKey)
      children.push(createFeatureManagerMaterialNode({
        key: materialKey,
        materialLabel: materialLabel ?? undefined,
        type: 'group',
      }))
    }
  }

  return children
}

function findBodyNodeByBodyId(node: GlbHierarchyNode, bodyId: string): GlbHierarchyNode | undefined {
  for (const child of node.children) {
    if (child.nodeType === 'Body' && child.meta?.bodyEntries?.[0]?.bodyId === bodyId) {
      return child
    }
    const nested = findBodyNodeByBodyId(child, bodyId)
    if (nested) {
      return nested
    }
  }
  return undefined
}

function resolveNodeSelectionObjectId(node: GlbHierarchyNode | null | undefined): string | null {
  if (!node) {
    return null
  }
  const explicitSelectionObjectId = typeof node.meta?.selectionObjectId === 'string'
    ? node.meta.selectionObjectId.trim()
    : ''
  if (explicitSelectionObjectId.length > 0) {
    return explicitSelectionObjectId
  }

  const externalFileUuid = node.meta?.externalFileUuid?.trim()
  if (node.selectable || externalFileUuid) {
    return node.objectId
  }

  for (const child of node.children) {
    const resolved = resolveNodeSelectionObjectId(child)
    if (resolved) {
      return resolved
    }
  }
  return null
}

function buildHierarchyNode(
  tabId: string,
  node: GlbHierarchyNode,
  context: GlbTreeBuildContext,
): TreeOption | null {
  const presentation = createGlbNodePresentationUpdate(tabId, node)
  const treeKey = presentation.key
  const selectionObjectId = resolveNodeSelectionObjectId(node)
  const refObjectId = resolveTreeNodeRefObjectId(node, selectionObjectId)
  const externalFileUuid = node.meta?.externalFileUuid?.trim() || undefined
  const externalAssemblyName = node.meta?.externalAssemblyName?.trim() || undefined
  const canFixOrFloat = presentation.canFixOrFloat
  const fixationState = presentation.fixationState
  const selectable = (node.selectable && Boolean(selectionObjectId)) || Boolean(externalFileUuid)
  const displayName = presentation.displayName

  const hierarchyChildren: TreeOption[] = []
  const promotedChildren: TreeOption[] = []
  node.children.forEach((child) => {
    // 虚拟 Body 节点（nodeType=Body）由 buildVirtualBodyNodes 注入，
    // 已通过 buildPartSpecificChildren 折叠到"实体(N)"文件夹，
    // 不再展开为独立层级子节点；保留 objectKeyBinding 以支持选中联动。
    if (child.nodeType === 'Body') {
      context.objectKeyBindings.push({
        objectId: child.objectId,
        key: treeKey,
      })
      return
    }

    if (isSyntheticPrimitiveNode(child)) {
      context.objectKeyBindings.push({
        objectId: child.objectId,
        key: treeKey,
      })
      child.children
        .map((grandchild) => buildHierarchyNode(tabId, grandchild, context))
        .filter((grandchild): grandchild is TreeOption => Boolean(grandchild))
        .forEach((grandchild) => hierarchyChildren.push(grandchild))
      buildPartSpecificChildren(
        treeKey,
        child,
        context,
        `${treeKey}::promoted::${child.objectId}`,
        selectionObjectId,
      ).forEach((promotedChild) => promotedChildren.push(promotedChild))
      return
    }

    const childNode = buildHierarchyNode(tabId, child, context)
    if (childNode) {
      hierarchyChildren.push(childNode)
    }
  })
  /**
   * datum-slot 的宿主应优先绑定到当前节点真实的选择锚点。
   * 这样在装配引用零件场景下，原点与三基准面会跟随实例 frame，
   * 而不是错误地落到 hierarchy 内部包装节点上。
   */
  const datumSlotHostObjectId = selectionObjectId ?? node.objectId
  const defaultSystemHeaderChildren = isLinearComponentPatternRuntimeNode(node)
    ? []
    : buildDefaultSystemHeaderChildren(treeKey, context)
  const defaultDatumSlotChildren = isLinearComponentPatternRuntimeNode(node)
    ? []
    : buildDefaultDatumSlotChildren(tabId, treeKey, datumSlotHostObjectId, context)
  const partSpecificChildren = buildPartSpecificChildren(treeKey, node, context, treeKey, selectionObjectId)
  // SolidWorks 的 FeatureManager 先展示系统文件夹，再展示基准项，最后才进入零件/装配业务结构。
  const children = [
    ...defaultSystemHeaderChildren,
    ...partSpecificChildren,
    ...defaultDatumSlotChildren,
    ...hierarchyChildren,
    ...promotedChildren,
  ]

  context.glbNodeBindings.push({
    key: treeKey,
    objectId: node.objectId,
    assemblyNodeId: node.meta?.assemblyNodeId?.trim() || undefined,
    selectionObjectId,
    objectType: resolveNodeObjectType(node),
    glbNodeRole: resolveNodeRole(node),
    glbPath: node.path,
    selectable,
    displayName,
    assemblyNodeDisplayName: resolveAssemblyNodeDisplayName(node),
    assemblyNodeInstanceIndex: node.meta?.instanceIndex,
    externalFileUuid,
    externalAssemblyName,
    fixationState,
    canFixOrFloat,
    highlightTargets: resolveGlbHierarchyHighlightTargets(node),
  })
  context.objectKeyBindings.push({
    objectId: node.objectId,
    key: treeKey,
  })

  return {
    key: treeKey,
    label: displayName,
    type: 'glb-node',
    iconType: resolveGlbHierarchyNodeIconType(node),
    //------ 线性零部件阵列节点包围盒移入 开始------
    /**
     * 标记运行时线性零部件阵列节点，供 FeatureTree 复用 imported-model 包围盒 hover 与多目标选择链路。
     * 这里只写入纯字符串标记，不把 Three.js 对象或 loader 运行时状态泄漏到 UI 层。
     */
    linearComponentPatternRuntimeNodeType: isLinearComponentPatternRuntimeNode(node)
      ? node.nodeType
      : undefined,
    //------ 线性阵列来源对象粉色高亮 开始------
    /**
     * 分组节点关联的源零部件对象 ID。点击分组时，UI 会把该列表作为来源高亮元信息传给 cad-core。
     */
    linearComponentPatternSourceObjectIds: resolveLinearComponentPatternSourceObjectIds(node),
    //------ 线性阵列来源对象粉色高亮 结束------
    //------ 线性零部件阵列节点包围盒移入 结束------
    isLeaf: children.length === 0,
    disabled: shouldDisableGlbTreeNode(node, selectable),
    children: children.length > 0 ? children : undefined,
    refObjectId: refObjectId ?? undefined,
  }
}

/**
 * 创建一次 GLB 设计树构建使用的独立上下文。
 *
 * @param useAuthoritativeBodySelection Part 根是否必须使用 Worker 几何修订 ID 作为选择身份。
 * @returns 不与其他完整树或片段构建共享的空上下文。
 */
function createGlbTreeBuildContext(useAuthoritativeBodySelection: boolean): GlbTreeBuildContext {
  return {
    glbNodeBindings: [],
    bodyNodeBindings: [],
    datumSlotBindings: [],
    objectKeyBindings: [],
    groupKeys: [],
    useAuthoritativeBodySelection,
  }
}

/**
 * 仅构建指定 GLB hierarchy 节点的设计树片段。
 *
 * 该入口用于稳定装配根下的运行时子树增量替换：不会折叠包装根、不会注入场景草图，
 * 也不会重新创建装配根投影，因此可以保留无关节点引用、展开状态和选择状态。
 *
 * @param tabId 当前设计树标签 ID。
 * @param hierarchyNodes 需要投影为直接子树的 hierarchy 节点。
 * @returns 片段树及其完整绑定集合。
 */
export function buildGlbFeatureManagerTreeFragments(
  tabId: string,
  hierarchyNodes: readonly GlbHierarchyNode[],
): GlbFeatureManagerTreeBuildResult {
  const context = createGlbTreeBuildContext(false)
  const tree = hierarchyNodes
    .map((node) => buildHierarchyNode(tabId, node, context))
    .filter((node): node is TreeOption => Boolean(node))

  return {
    tree,
    glbNodeBindings: context.glbNodeBindings,
    bodyNodeBindings: context.bodyNodeBindings,
    datumSlotBindings: context.datumSlotBindings,
    objectKeyBindings: context.objectKeyBindings,
    groupKeys: context.groupKeys,
    sketchNodeBindings: [],
  }
}

export function buildGlbFeatureManagerTree(
  options: GlbFeatureManagerTreeBuildOptions,
): GlbFeatureManagerTreeBuildResult {
  const context = createGlbTreeBuildContext(options.rootIconType === 'part')

  // 折叠同名单子节点包装根（SolidWorks 语义：当场景根节点仅为同名包装层时，
  // 直接提升其唯一语义子节点作为树根，包装节点的 objectKeyBinding 重定向到子节点 key）
  const rootHierarchy = options.hierarchy
  const shouldCollapseRuntimeWrapper =
    rootHierarchy.meta?.runtimeWrapper === true
    && rootHierarchy.children.length === 1
  const shouldCollapseWrapper =
    shouldCollapseRuntimeWrapper
    || (
    !rootHierarchy.selectable
    && (rootHierarchy.meta?.semanticKind == null || rootHierarchy.meta.semanticKind === 'unknown')
    && rootHierarchy.children.length === 1
    && rootHierarchy.children[0].name === rootHierarchy.name
    )

  const effectiveHierarchy = shouldCollapseWrapper
    ? rootHierarchy.children[0]
    : rootHierarchy

  const hierarchyNode = buildHierarchyNode(options.tabId, effectiveHierarchy, context)
  if (!hierarchyNode) {
    return {
      tree: [],
      glbNodeBindings: context.glbNodeBindings,
      bodyNodeBindings: context.bodyNodeBindings,
      datumSlotBindings: context.datumSlotBindings,
      objectKeyBindings: context.objectKeyBindings,
      groupKeys: [],
      sketchNodeBindings: [],
    }
  }

  // 若折叠了包装根，将包装根自身 objectId 也绑定到提升后的子节点 key
  if (shouldCollapseWrapper) {
    const semanticKey = String(hierarchyNode.key ?? '')
    context.objectKeyBindings.push({
      objectId: rootHierarchy.objectId,
      key: semanticKey,
    })
  }

  const treeRoot: TreeOption = {
    ...hierarchyNode,
    label: hierarchyNode.label ?? options.rootLabel ?? DEFAULT_ROOT_LABEL,
    iconType: options.rootIconType ?? (hierarchyNode as { iconType?: string }).iconType ?? 'group',
  }

  // 把 viewer 当前的动态草图追加到根节点子节点末尾。
  // 这是 hybrid 路径的核心：GLB 静态结构 + 用户在零件中创建的动态草图共存于同一棵树，
  // 既不丢失 GLB 实体（之前路由到 projection 的 bug），也能让空零件中绘制的草图出现在树上。
  // 装配体若无草图（当前视为静态），sceneSketches 为空，注入逻辑等价于 no-op。
  const sketchNodeBindings: GlbFeatureTreeSketchNodeBinding[] = []
  if (options.sceneSketches && options.sceneSketches.length > 0) {
    const existingChildren = Array.isArray(treeRoot.children)
      ? (treeRoot.children as TreeOption[])
      : []
    const sketchTreeNodes: TreeOption[] = options.sceneSketches.map((sketch) => {
      const sketchKey = `sketch-${sketch.id}`
      sketchNodeBindings.push({ key: sketchKey, objectId: sketch.id })
      return {
        key: sketchKey,
        label: sketch.name,
        type: 'sketch',
        iconType: 'sketch',
        isLeaf: true,
        refObjectId: sketch.id,
        statusHidden: sketch.hidden ?? false,
        statusSuppressed: sketch.suppressed ?? false,
      } as TreeOption
    })
    treeRoot.children = [...existingChildren, ...sketchTreeNodes]
    treeRoot.isLeaf = false
  }

  return {
    tree: [treeRoot],
    glbNodeBindings: context.glbNodeBindings,
    bodyNodeBindings: context.bodyNodeBindings,
    datumSlotBindings: context.datumSlotBindings,
    objectKeyBindings: context.objectKeyBindings,
    groupKeys: context.groupKeys,
    sketchNodeBindings,
  }
}
